import React from 'react';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import styles from './index.module.scss';
import phonicsArrow from '@/assets/productCenter/phonics/phonics-arrow.png';
import phonicsLevel1 from '@/assets/productCenter/phonics/phonics-level-1.png';
import phonicsLevel2 from '@/assets/productCenter/phonics/phonics-level-2.png';
import phonicsLevel3 from '@/assets/productCenter/phonics/phonics-level-3.png';
import phonicsCardLight1 from '@/assets/productCenter/phonics/phonics-card-light-1.png';
import phonicsCardLight2 from '@/assets/productCenter/phonics/phonics-card-light-2.png';
import phonicsCardLight3 from '@/assets/productCenter/phonics/phonics-card-light-3.png';
import phonicsCardDark1 from '@/assets/productCenter/phonics/phonics-card-dark-1.png';
import phonicsCardDark2 from '@/assets/productCenter/phonics/phonics-card-dark-2.png';
import phonicsCardDark3 from '@/assets/productCenter/phonics/phonics-card-dark-3.png';
import phonicsDot1 from '@/assets/productCenter/phonics/phonics-dot-1.png';
import phonicsDot2 from '@/assets/productCenter/phonics/phonics-dot-2.png';
import phonicsDot3 from '@/assets/productCenter/phonics/phonics-dot-3.png';
import phonicsDivide from '@/assets/productCenter/phonics/phonics-divide.png';
import phonicsDivide2 from '@/assets/productCenter/phonics/phonics-divide2.png';

const SCROLL_CONTAINER = '[data-scroll-root="aixue-phonics"]';

const LEVELS = [
  {
    id: 'L1',
    name: '初级',
    levelImg: phonicsLevel1,
    phonicsDot: phonicsDot1,
    cardBg: phonicsCardLight1,
    cardBgDark: phonicsCardDark1,
    items: [
      '建立26个字母的音形对应，发现字母不同结构的发音规律。',
      '拼读CVC和CVCe单词，体会见词读、听音写的成就感。',
      '解决"不敢读、不会读"的单词认读难题。',
    ],
    darkItems: ['40 节课', '26 个字母 + 5 组长短元音', '168 个核心词', '7 本绘本'],
  },
  {
    id: 'L2',
    name: '中级',
    levelImg: phonicsLevel2,
    phonicsDot: phonicsDot2,
    cardBg: phonicsCardLight2,
    cardBgDark: phonicsCardDark2,
    items: [
      '探索字母组合的发音规律。',
      '拼读CCVC, CWC, CVCCE词，感受词汇量提升带来的成就感。',
      '摆脱"死记硬背"的单词背诵痛苦。',
    ],
    darkItems: ['40 节课', '58 个字母', '168 个核心词', '7 本绘本'],
  },
  {
    id: 'L3',
    name: '高级',
    levelImg: phonicsLevel3,
    phonicsDot: phonicsDot3,
    cardBg: phonicsCardLight3,
    cardBgDark: phonicsCardDark3,
    items: [
      '探索更多字母组合的发音规律。',
      '拆分多音节单词、认读合成词、押韵词，培养拼读优势，增加自我效能感。',
      '化解"长难词、复杂词"的单词记忆困境。',
    ],
    darkItems: ['40 节课', '48 个字母组合', '多音节、合成词、押韵词', '168 个核心词', '7 本绘本'],
  },
];

function LevelCard({
  name,
  levelImg,
  phonicsDot,
  cardBg,
  cardBgDark,
  items,
  darkItems,
  cardClassName,
}: {
  name: string;
  levelImg: string;
  phonicsDot: string;
  cardBg: string;
  cardBgDark: string;
  items: string[];
  darkItems: string[];
  cardClassName?: string;
}) {
  return (
    <div className={`${styles['screen3-card']} ${cardClassName ?? ''}`}>
      <div
        className={styles['screen3-card-top']}
        style={{ backgroundImage: `url(${levelImg})` }}
        role="img"
        aria-label={name}
      >
        <span className={styles['screen3-card-name']}>{name}</span>
      </div>
      <div className={styles['screen3-card-bottom']}>
        {/* light 作为底层背景，固定显示不消失；dark 滑下时叠在上方 */}
        <div
          className={styles['screen3-card-bottom-light']}
          style={{ backgroundImage: `url(${cardBg})` }}
          aria-hidden
        />
        <div className={styles['screen3-card-list']}>
          {items.map((text, i) => (
            <div key={i} className={styles['screen3-card-item']}>
              <img src={phonicsDot} alt="" className={styles['screen3-dot']} loading="lazy" />
              <div className={styles['screen3-card-item-content']}>
                <span className={styles['screen3-card-txt']}>{text}</span>
                {i < items.length - 1 && (
                  <img
                    src={phonicsDivide}
                    alt=""
                    className={styles['screen3-divide']}
                    aria-hidden
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        {/* 模糊层作为 card-bottom 直接子元素，backdrop 才能正确模糊背后的 light */}
        <div className={styles['screen3-card-dark-blur']} aria-hidden />
        <div className={styles['screen3-card-bottom-dark']} aria-hidden>
          <div
            className={styles['screen3-card-dark-bg']}
            style={{ backgroundImage: `url(${cardBgDark})` }}
            aria-hidden
          />
          <div className={styles['screen3-card-list-dark']}>
            {darkItems.map((text, i) => (
              <div key={i} className={styles['screen3-card-item']}>
                <div className={styles['screen3-card-item-content']}>
                  <span className={styles['screen3-card-txt-dark']}>
                    {text.split(/(\d+)/).map((part, j) =>
                      /^\d+$/.test(part) ? (
                        <span key={j} className={styles['screen3-card-txt-dark-num']}>
                          {part}
                        </span>
                      ) : (
                        part
                      ),
                    )}
                  </span>
                  {i < darkItems.length - 1 && (
                    <img
                      src={phonicsDivide2}
                      alt=""
                      className={styles['screen3-divide']}
                      aria-hidden
                      loading="lazy"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Screen3() {
  const screen3Ref = useScreenSlideIn({
    scrollContainerSelector: SCROLL_CONTAINER,
    animationClassName: styles['screen3-anim'],
    triggerSlideRatio: 0.6,
  });

  return (
    <section
      ref={screen3Ref.ref as React.RefObject<HTMLElement>}
      className={styles['screen3-cover']}
    >
      <div className={styles['screen3-container']}>
        <h2 className={styles['screen3-title']}>AI自然拼读</h2>
        <div className={styles['screen3-path']}>
          <img src={phonicsArrow} alt="" className={styles['screen3-arrow']} aria-hidden loading="lazy" />
          <div className={styles['screen3-cards']}>
            {LEVELS.map((level, i) => (
              <LevelCard
                key={level.id}
                name={level.name}
                phonicsDot={level.phonicsDot}
                levelImg={level.levelImg}
                cardBg={level.cardBg}
                cardBgDark={level.cardBgDark}
                items={level.items}
                darkItems={level.darkItems}
                cardClassName={
                  [
                    styles['screen3-card--l1'],
                    styles['screen3-card--l2'],
                    styles['screen3-card--l3'],
                  ][i]
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
